import type {
  ScaffolderStep,
  ScaffolderTask,
  ScaffolderTaskOutput,
} from '@backstage/plugin-scaffolder-react';

/**
 * What the task page of the deletion templates (`project-delete`, those
 * another AppType designates) reads from the task: its steps in phases,
 * their status in words, and the structured report of the cleanup step.
 * Pure: `ProjectDeletionTaskPage.tsx` renders it.
 */

/** A step's status on the page. */
export type StepState =
  | 'done'
  | 'running'
  | 'pending'
  | 'failed'
  | 'skipped'
  | 'cancelled';

/**
 * The scaffolder's step status (`TaskStatus`, plugin-scaffolder-common), as
 * the page names it.
 */
const STATES: Record<ScaffolderStep['status'], StepState> = {
  completed: 'done',
  processing: 'running',
  open: 'pending',
  failed: 'failed',
  skipped: 'skipped',
  cancelled: 'cancelled',
};

/** The phases of the timeline, in order, with the step ids of each. */
export const STEP_PHASES: ReadonlyArray<{ title: string; ids: string[] }> = [
  { title: 'Préparation', ids: ['inventory', 'freeze'] },
  {
    title: 'Démontage',
    ids: [
      'teardown-ephemeral',
      'teardown-sleep',
      'teardown-application',
      'teardown-release',
      'teardown-buckets',
      'teardown-crossplane',
      'teardown-appenv',
    ],
  },
  { title: 'Finalisation', ids: ['cleanup'] },
];

/** The phase of the steps the timeline does not know (a template change). */
export const OTHER_PHASE = 'Autres étapes';

/** The grey line under each step's name: what the step goes through. */
export const STEP_DETAILS: Record<string, string> = {
  inventory: 'lecture des dépôts, environnements, buckets…',
  freeze: 'description, topic, CI',
  'teardown-ephemeral': 'ApplicationSet et environnements éphémères',
  'teardown-sleep': 'autres environnements mis en sommeil, buckets vidés',
  'teardown-application': 'Application ArgoCD, volumes',
  'teardown-release': 'conservation levée, buckets en sommeil repris',
  'teardown-buckets': 'sauvegarde, envoi et buckets repris',
  'teardown-crossplane': 'sauvegarde, import, observabilité',
  'teardown-appenv': 'namespace, AppProject, dossiers des environnements',
  cleanup: 'Grafana, LLDAP, catalogue, registre',
};

export interface StepView {
  id: string;
  /** Its short name on the page, else the step's name in the task spec. */
  name: string;
  detail?: string;
  state: StepState;
  /** Its position in the timeline, from 1. */
  number: number;
  /**
   * Milliseconds: for a finished step whose start and end are known; for
   * the running step, since its start, when the caller gives `now`.
   */
  durationMs?: number;
}

export interface PhaseView {
  title: string;
  steps: StepView[];
}

/**
 * How a template's steps are laid out on its task page: the phases and the
 * step ids of each, the short names (the spec's name without one) and the
 * grey line under each name.
 */
export interface StepLayout {
  phases: ReadonlyArray<{ title: string; ids: string[] }>;
  names?: Readonly<Record<string, string>>;
  details: Readonly<Record<string, string>>;
  /**
   * Étapes affichées comme une partie d'une autre : id de la partie → id de
   * l'étape qui l'affiche. Une partie n'a pas de ligne à elle quand son
   * étape est dans la tâche ; l'état et la durée de la ligne sont ceux de
   * l'ensemble (`foldedState`). Une tâche sans ces parties s'affiche comme
   * sans cette table.
   */
  parts?: Readonly<Record<string, string>>;
  /**
   * Steps of the template that this task does not run and that the page
   * leaves out: those of the other operations of a template whose steps
   * each run for one operation only (`env-restore`, `environmentTask.ts`).
   */
  omit?: ReadonlyArray<string>;
}

/**
 * L'état d'une ligne faite de plusieurs étapes : en échec, en cours ou
 * annulée dès qu'une l'est ; faite quand toutes sont faites ou ignorées et
 * au moins une faite ; ignorée quand toutes le sont ; en attente sinon —
 * entre deux de ses étapes aussi, comme la tâche entre deux lignes.
 */
export function foldedState(states: StepState[]): StepState {
  for (const state of ['failed', 'running', 'cancelled'] as const) {
    if (states.includes(state)) return state;
  }
  if (states.every(state => state === 'skipped')) return 'skipped';
  if (states.every(state => state === 'done' || state === 'skipped')) {
    return 'done';
  }
  return 'pending';
}

/** The layout of the deletion templates. */
export const DELETION_LAYOUT: StepLayout = {
  phases: STEP_PHASES,
  details: STEP_DETAILS,
};

/**
 * The task's steps, in phases: each phase lists its steps in the order of
 * the task spec, the unknown ids last in {@link OTHER_PHASE}. The status
 * and times come from the event stream; a step it has not reported yet is
 * pending. With `now`, the running step's duration is its time so far.
 * Les parties (`StepLayout.parts`) sont comptées dans la ligne de leur étape.
 */
export function stepPhases(
  task: ScaffolderTask,
  streamed: Record<string, ScaffolderStep>,
  layout: StepLayout = DELETION_LAYOUT,
  now?: number,
): PhaseView[] {
  const specIds = new Set(task.spec.steps.map(step => step.id));
  // Les parties dont l'étape est dans la tâche, rangées sous cette étape.
  const membersOf = new Map<string, string[]>();
  for (const step of task.spec.steps) {
    const parent = layout.parts?.[step.id];
    if (parent === undefined || !specIds.has(parent)) continue;
    membersOf.set(parent, [...(membersOf.get(parent) ?? []), step.id]);
  }
  const folded = new Set([...membersOf.values()].flat());
  const omitted = new Set(layout.omit ?? []);
  const shown = task.spec.steps.filter(
    step => !folded.has(step.id) && !omitted.has(step.id),
  );
  const known = new Set(layout.phases.flatMap(phase => phase.ids));
  const groups = [
    ...layout.phases.map(phase => ({
      title: phase.title,
      specSteps: shown.filter(step => phase.ids.includes(step.id)),
    })),
    {
      title: OTHER_PHASE,
      specSteps: shown.filter(step => !known.has(step.id)),
    },
  ];
  let number = 0;
  return groups
    .filter(group => group.specSteps.length > 0)
    .map(group => ({
      title: group.title,
      steps: group.specSteps.map(step => {
        number += 1;
        const ids = [step.id, ...(membersOf.get(step.id) ?? [])];
        const statuses = ids.map(id => streamed[id]);
        const state = foldedState(
          statuses.map(status => (status ? STATES[status.status] : 'pending')),
        );
        const times = (key: 'startedAt' | 'endedAt') =>
          statuses
            .map(status => {
              const at = status?.[key];
              return at ? Date.parse(at) : NaN;
            })
            .filter(Number.isFinite);
        const starts = times('startedAt');
        const ends = times('endedAt');
        // Une seule étape : ses propres temps, comme avant les parties.
        // Plusieurs : du premier départ à la dernière fin ; en cours, la fin
        // d'une partie déjà faite n'arrête pas le compteur.
        const started = starts.length > 0 ? Math.min(...starts) : NaN;
        const ended =
          ends.length > 0 && (ids.length === 1 || state !== 'running')
            ? Math.max(...ends)
            : NaN;
        const view: StepView = {
          id: step.id,
          name: layout.names?.[step.id] ?? step.name,
          state,
          number,
        };
        const detail = layout.details[step.id];
        if (detail) view.detail = detail;
        if (Number.isFinite(started) && Number.isFinite(ended)) {
          view.durationMs = Math.max(0, ended - started);
        } else if (
          state === 'running' &&
          now !== undefined &&
          Number.isFinite(started)
        ) {
          view.durationMs = Math.max(0, now - started);
        }
        return view;
      }),
    }));
}

/**
 * A step's duration in French words, as short as the pill allows: « < 1 s »,
 * « 6 s », « 2 min 05 s », « 1 h 02 min ».
 */
export function frenchDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 1) return '< 1 s';
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ${String(seconds % 60).padStart(2, '0')} s`;
  }
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${String(minutes % 60).padStart(2, '0')} min`;
}

/** A step's status in words, its duration after « Fait » when known. */
export function stepStatusLabel(step: StepView): string {
  switch (step.state) {
    case 'done':
      return step.durationMs === undefined
        ? 'Fait'
        : `Fait · ${frenchDuration(step.durationMs)}`;
    case 'running':
      return step.durationMs === undefined
        ? 'En cours'
        : `En cours · ${frenchDuration(step.durationMs)}`;
    case 'failed':
      return 'Échec';
    case 'skipped':
      return 'Ignorée';
    case 'cancelled':
      return 'Annulée';
    default:
      return 'En attente';
  }
}

/** Mirror of `ReportData` (backend `projectDelete/finalReport.ts`). */
export type RowOutcome = 'removed' | 'changed' | 'noop' | 'error';

/**
 * A row's group: its outcome, but for the buckets emptied (`removed` in the
 * figures, « vidés » here).
 */
export type RowKind = 'removed' | 'emptied' | 'changed' | 'noop' | 'error';

const ROW_KINDS: RowKind[] = ['removed', 'emptied', 'changed', 'noop', 'error'];

export interface ReportRow {
  element: string;
  detail: string;
  action: string;
  outcome: RowOutcome;
  /** Absent from the output of a backend from before the groups. */
  kind?: RowKind;
  /** The row's objects, plain text; absent as `kind`. */
  items?: string[];
  /**
   * `gitlab`, `argocd`, `bucket`, `crossplane`, `grafana`, `lldap`,
   * `catalog`, `file`; read as any string, an unknown one drawing nothing.
   */
  icon?: string;
}

export interface ReportData {
  mode: 'simulation' | 'deletion';
  project: string;
  outcome: 'ok' | 'partial';
  figures: { removed: number; noop: number; manual: number };
  rows: ReportRow[];
  manual: Array<{
    text: string;
    links?: Array<{ label: string; url: string }>;
    commands?: Array<{ intro?: string; shell: string }>;
  }>;
  notes: string[];
  warnings: string[];
  nextStepHref?: string;
}

/** The output key the template gives the cleanup's `reportData` under. */
export const REPORT_OUTPUT_KEY = 'deletionReport';

type Guard = (value: unknown) => boolean;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isString: Guard = value => typeof value === 'string';
const isCount: Guard = value =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;
const arrayOf =
  (guard: Guard): Guard =>
  value =>
    Array.isArray(value) && value.every(guard);
const optional =
  (guard: Guard): Guard =>
  value =>
    value === undefined || guard(value);
const shape =
  (fields: Record<string, Guard>): Guard =>
  value =>
    isRecord(value) &&
    Object.entries(fields).every(([key, guard]) => guard(value[key]));
const oneOf =
  (...values: string[]): Guard =>
  value =>
    typeof value === 'string' && values.includes(value);

const isReportData = shape({
  mode: oneOf('simulation', 'deletion'),
  project: isString,
  outcome: oneOf('ok', 'partial'),
  figures: shape({ removed: isCount, noop: isCount, manual: isCount }),
  rows: arrayOf(
    shape({
      element: isString,
      detail: isString,
      action: isString,
      outcome: oneOf('removed', 'changed', 'noop', 'error'),
      kind: optional(oneOf(...ROW_KINDS)),
      items: optional(arrayOf(isString)),
      icon: optional(isString),
    }),
  ),
  manual: arrayOf(
    shape({
      text: isString,
      links: optional(arrayOf(shape({ label: isString, url: isString }))),
      commands: optional(
        arrayOf(shape({ intro: optional(isString), shell: isString })),
      ),
    }),
  ),
  notes: arrayOf(isString),
  warnings: arrayOf(isString),
  nextStepHref: optional(isString),
});

/**
 * The structured report of a finished task, or undefined when the output has
 * none — a template or a backend from before `reportData`, whose page then
 * shows the markdown outputs — or one of another shape.
 */
export function readReportData(
  output: ScaffolderTaskOutput | undefined,
): ReportData | undefined {
  const value = output?.[REPORT_OUTPUT_KEY];
  return isReportData(value) ? (value as ReportData) : undefined;
}

/** The tone of a group's pill: BUI's status tokens. */
export type GroupTone = 'success' | 'info' | 'warning' | 'danger';

/** One element of a group: its icon key, its name, its objects. */
export interface GroupRowView {
  element: string;
  icon?: string;
  /** Plain text, one chip each; inline markdown when `markdown`. */
  items: string[];
  /**
   * A row without `items` (an older backend): its `detail`, inline markdown,
   * as its one object.
   */
  markdown: boolean;
}

export interface RowGroupView {
  kind: RowKind;
  title: string;
  tone: GroupTone;
  rows: GroupRowView[];
}

/** The groups in their order on the page: errors first. */
const GROUPS: ReadonlyArray<{
  kind: RowKind;
  tone: GroupTone;
  title: Record<ReportData['mode'], string>;
}> = [
  {
    kind: 'error',
    tone: 'danger',
    title: { simulation: 'En erreur', deletion: 'En erreur' },
  },
  {
    kind: 'removed',
    tone: 'danger',
    title: { simulation: 'Seraient supprimés', deletion: 'Supprimés' },
  },
  {
    kind: 'emptied',
    tone: 'warning',
    title: { simulation: 'Seraient vidés', deletion: 'Vidés' },
  },
  {
    kind: 'changed',
    tone: 'info',
    title: {
      simulation: 'Seraient marqués ou enregistrés',
      deletion: 'Marqués ou enregistrés',
    },
  },
  {
    kind: 'noop',
    tone: 'success',
    title: {
      simulation: 'Déjà fait / sans objet',
      deletion: 'Déjà fait / sans objet',
    },
  },
];

/**
 * « Ce qui serait fait / a été fait » by kind of action, the empty groups
 * left out. In a group, the rows of one element are one (their objects
 * joined, each once): the backend keeps apart the rows of one element whose
 * action differs (« sans objet », « déjà fait »), which the page does not
 * show. A row from a backend without `kind` and `items` is grouped by its
 * outcome, its detail its one object.
 */
export function rowGroups(report: ReportData): RowGroupView[] {
  return GROUPS.map(group => {
    const rows: GroupRowView[] = [];
    for (const row of report.rows) {
      if ((row.kind ?? row.outcome) !== group.kind) continue;
      const markdown = row.items === undefined;
      const items = row.items ?? [row.detail];
      const same = rows.find(
        view =>
          view.element === row.element &&
          view.icon === row.icon &&
          view.markdown === markdown &&
          group.kind !== 'error',
      );
      if (same) {
        same.items.push(...items.filter(item => !same.items.includes(item)));
      } else {
        rows.push({
          element: row.element,
          ...(row.icon !== undefined ? { icon: row.icon } : {}),
          items: [...items],
          markdown,
        });
      }
    }
    return {
      kind: group.kind,
      title: group.title[report.mode],
      tone: group.tone,
      rows,
    };
  }).filter(group => group.rows.length > 0);
}
